import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Subject, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import { BackendApiService } from 'src/app/core/services/backend-api';
import {
  MyPurchaseForm,
  UserInformation,
} from 'src/app/shared/models/app-models';
import {
  CheckRFSReadyRequest,
  CheckRFSReadyResponse,
  PEAssignmentListResponse,
  ReadyTaggingRequest,
  SaveTaggingRequest,
  UnreadyTaggingRequest,
} from 'src/app/shared/models/database-models/RFS.model';
import { Vendor } from 'src/app/shared/models/database-models';
import _ from 'lodash';
import {
  ValidationErrorCategory,
  ValidationErrorMessage,
} from 'src/app/shared/models/app-models/ValidationErrorMessage.model';
import { ValidationService } from 'src/app/shared/services/validation.service';
import {
  itemListFrontendToBackendMapper,
  taggedGroupsFrontendToBackendMapper,
  vendorsListFrontendToBackendMapper,
} from '../../shared/utils/format-form-data-for-save';
import {
  InvalidTaggedGroupReason,
  TaggedGroupsForPosting,
  TagItemVendorService,
} from './tag-item-vendor.service';
import { validateVendorsEmail } from '../../shared/utils/validate-vendor-email';
import SourcingStatusJSON from 'src/app/data/constants/SourcingStatus.json';
import { environment } from 'src/environments/environment';

interface AssignmentListData extends PEAssignmentListResponse {
  MPDocumentNumber: string;
  requestorUsername: string;
}

export interface AssignmentList {
  error: string;
  isLoading: boolean;
  data: AssignmentListData | null;
}

const INITIAL_ASSIGNMENT_LIST: AssignmentList = {
  error: '',
  isLoading: false,
  data: null,
};

interface RFSReadyData extends Pick<CheckRFSReadyResponse, 'roleId' | 'role'> {
  MPDocumentNumber: string;
  requestorUsername: string;
  isRFSReadied: boolean;
}

export interface RFSReady {
  error: string;
  isLoading: boolean;
  data: RFSReadyData | null;
}

const INITIAL_RFS_READY: RFSReady = {
  error: '',
  isLoading: false,
  data: null,
};

@Injectable({ providedIn: 'root' })
export class RfsService implements OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private form!: MyPurchaseForm;

  private fetchedAssignmentList = new BehaviorSubject<AssignmentList>(
    INITIAL_ASSIGNMENT_LIST
  );
  public assignmentList$ = this.fetchedAssignmentList.asObservable();

  private fetchedRFSReady = new BehaviorSubject<RFSReady>(INITIAL_RFS_READY);
  public rfsReady$ = this.fetchedRFSReady.asObservable();

  private mpDocumentNumber: string | null = null;
  private loggedInUsername: string | null = null;

  constructor(
    private backendApiService: BackendApiService,
    private validationService: ValidationService,
    private store: Store<{ user: UserInformation; form: MyPurchaseForm }>,
    private tagItemVendorService: TagItemVendorService
  ) {
    this.store
      .select('form')
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (form: MyPurchaseForm) => {
        this.form = form;

        const oldMPDocumentNumber = this.mpDocumentNumber;
        this.mpDocumentNumber = this.form.documentNo;

        // Re-fetch assignment list if MPDocumentNumber changes
        if (
          this.mpDocumentNumber &&
          this.mpDocumentNumber !== oldMPDocumentNumber &&
          this.loggedInUsername
        ) {
          await this.onFetchAssignmentList();
        }
      });

    this.store
      .select('user')
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (user: UserInformation) => {
        const oldLoggedInUsername = this.loggedInUsername;
        this.loggedInUsername = user.userName;

        // Re-fetch assignment list if loggedInUsername changes
        if (
          this.loggedInUsername &&
          this.loggedInUsername !== oldLoggedInUsername &&
          this.mpDocumentNumber
        ) {
          await this.onFetchAssignmentList();
        }
      });

    this.assignmentList$
      .pipe(takeUntil(this.destroy$))
      .subscribe((assignmentList: AssignmentList) => {
        if (
          assignmentList.data &&
          assignmentList.data.MPDocumentNumber === this.mpDocumentNumber
        ) {
          this.onFetchRFSReady();
        }
      });
  }

  public async onFetchAssignmentList(): Promise<AssignmentList> {
    if (
      !this.mpDocumentNumber ||
      !this.loggedInUsername ||
      (this.form.documentStatusId !== 15 &&
        this.form.documentSubStatusId !== 25)
    ) {
      // Clear assignment list && rfsReady if missing parameters or documentStatus does not match
      const emptyResult = { error: '', isLoading: false, data: null };
      this.fetchedAssignmentList.next(emptyResult);
      this.fetchedRFSReady.next(emptyResult);
      return emptyResult;
    }

    this.fetchedAssignmentList.next({ error: '', isLoading: true, data: null });

    try {
      const mpDocumentNumber = this.mpDocumentNumber;
      const requestorUsername = this.loggedInUsername;
      const response: PEAssignmentListResponse = await lastValueFrom(
        this.backendApiService.getAssignmentList(
          mpDocumentNumber,
          requestorUsername
        )
      );

      const assignmentList = {
        error: '',
        isLoading: false,
        data: {
          MPDocumentNumber: mpDocumentNumber,
          requestorUsername: requestorUsername,
          ...response,
        },
      };

      this.fetchedAssignmentList.next(assignmentList);

      return assignmentList;
    } catch (err: any) {
      const assignmentList = {
        error: err?.message ?? 'Something went wrong.',
        isLoading: false,
        data: null,
      };

      this.fetchedAssignmentList.next(assignmentList);
      return assignmentList;
    }
  }

  public async onFetchRFSReady(): Promise<RFSReady | null> {
    if (
      !this.mpDocumentNumber ||
      !this.loggedInUsername ||
      !this.fetchedAssignmentList.value.data ||
      !this.fetchedAssignmentList.value.data.dagId ||
      this.form.documentStatusId !== 15
    ) {
      // Clear rfsReady if missing parameters or documentStatus does not match
      const emptyResult = { error: '', isLoading: false, data: null };
      this.fetchedRFSReady.next(emptyResult);
      return emptyResult;
    }

    this.fetchedRFSReady.next({ error: '', isLoading: true, data: null });

    try {
      const reqBody: CheckRFSReadyRequest = {
        MPDocumentNumber: this.mpDocumentNumber,
        actioner: this.loggedInUsername,
        dagId: this.fetchedAssignmentList.value.data.dagId,
      };
      const response: CheckRFSReadyResponse = await lastValueFrom(
        this.backendApiService.checkRFSReady(reqBody)
      );

      if (!response) {
        console.log('Unable to get response from checkRFSReady.', response);
        throw new Error('Unable to get response from checkRFSReady.');
      }

      const rfsReady = {
        error: '',
        isLoading: false,
        data: {
          MPDocumentNumber: reqBody.MPDocumentNumber,
          requestorUsername: reqBody.actioner,
          isRFSReadied: !!response.ready,
          roleId: response.roleId,
          role: response.role,
        },
      };

      this.fetchedRFSReady.next(rfsReady);

      return rfsReady;
    } catch (err: any) {
      const rfsReady = {
        error: err?.message ?? 'Something went wrong.',
        isLoading: false,
        data: null,
      };
      this.fetchedRFSReady.next(rfsReady);

      return rfsReady;
    }
  }

  public async onPostRFSSave() {
    const mpDocumentNumber = this.mpDocumentNumber;

    if (!mpDocumentNumber) {
      return {
        isSuccess: false,
        errorMessage: `Missing required request parameters for API: mpDocumentNumber = ${mpDocumentNumber}`,
      };
    }
    const taggedGroupsObj = this.tagItemVendorService.taggedGroupsValue;
    if (mpDocumentNumber !== taggedGroupsObj.MPDocumentNumber) {
      return {
        isSuccess: false,
        errorMessage: `The MPDocumentNumber being saved does not match the MPDocumentNumber associated with the current taggings.`,
      };
    }

    const { validTaggedGroups, invalidTaggedGroups } =
      this.tagItemVendorService.getTaggedGroupsForPosting();
    // No validation applied when saving
    const taggedGroups = [
      ...validTaggedGroups,
      ...invalidTaggedGroups.map(({ taggedGroup }) => taggedGroup),
    ];

    let reqBody: SaveTaggingRequest = {
      MPDocumentNumber: mpDocumentNumber,
      itemList: [
        ...itemListFrontendToBackendMapper(this.form.ItemList, this.form),
      ],
      vendorsList: [
        ...vendorsListFrontendToBackendMapper(this.form.VendorsToSource),
      ],
      taggedGroups: taggedGroupsFrontendToBackendMapper(taggedGroups),
    };

    try {
      const response = await lastValueFrom(
        this.backendApiService.saveTagging(reqBody)
      );

      return { isSuccess: true };
    } catch (err: any) {
      const errorMessage = err?.message ?? 'Something went wrong.';

      return { isSuccess: false, errorMessage: errorMessage };
    }
  }

  public async validateRFSTaggedGroups(
    validTaggedGroups: TaggedGroupsForPosting['validTaggedGroups'],
    invalidTaggedGroups: TaggedGroupsForPosting['invalidTaggedGroups']
  ) {
    let validationErrorMessages: ValidationErrorMessage[] = [];
    const mpDocumentNumber = this.mpDocumentNumber;
    const vendorsList = this.form.VendorsToSource;
    const itemList = this.form.ItemList;

    /* 
      Check for invalid tagged group
    */
    const messageSet: Set<string> = new Set();
    for (const invalidTaggedGroup of invalidTaggedGroups) {
      const { taggedGroup } = invalidTaggedGroup;

      switch (invalidTaggedGroup.invalidReason) {
        case InvalidTaggedGroupReason.EMPTY_ITEM_NUMBERS:
        case InvalidTaggedGroupReason.TAGGED_TO_MISSING_DELETED_ITEM:
          if (
            Array.isArray(taggedGroup.vendors) &&
            taggedGroup.vendors.length > 0
          ) {
            messageSet.add(
              `Please review missing items tagged to <strong>(${taggedGroup.vendors
                .map((vendor) => vendor.vendorName)
                .join(', ')})</strong>.`
            );
          } else if (taggedGroup.sourcingStatusId) {
            messageSet.add(
              `Please review missing items tagged to <strong>"${this.getSourcingStatusNameFromId(
                taggedGroup.sourcingStatusId
              )}"</strong>.`
            );
          }
          break;
        case InvalidTaggedGroupReason.ITEM_ASSIGNED_TO_ANOTHER_PE:
        case InvalidTaggedGroupReason.TAGGED_TO_MISSING_DELETED_VENDOR:
          messageSet.add(
            `System conflict detected, please contact IT support.`
          );
          break;
        case InvalidTaggedGroupReason.MISSING_SOURCING_STATUS:
          messageSet.add(
            `You have one or more item-vendor set with missing sourcing status.`
          );
          break;
        case InvalidTaggedGroupReason.MISSING_VENDORS:
          if (taggedGroup.itemNumbers.length > 0) {
            messageSet.add(
              `Please review missing vendors tagged to <strong>(${taggedGroup.itemNumbers.join(
                ', '
              )})</strong>.`
            );
          }
          break;
      }
    }
    for (const uniqueMessage of Array.from(messageSet)) {
      validationErrorMessages.push({
        category: ValidationErrorCategory.Tagging,
        message: uniqueMessage,
      });
    }

    /*
      Check if every item assigned to current Purchase Executive has been sourced
    */
    const notSourcedItemNumbers = itemList
      .filter(
        (item) =>
          item.AddItems.PurchaseExecutive === this.loggedInUsername &&
          !item.isDeleted &&
          !item.Strikethrough &&
          !item.AddItems.SourcingStatusId
      )
      .map((item) => item.AddItems.displayItemNumber);

    if (notSourcedItemNumbers.length > 0) {
      validationErrorMessages.push({
        category: ValidationErrorCategory.Tagging,
        message: `Please review the items that have not been tagged: <strong>${notSourcedItemNumbers.join(
          ', '
        )}</strong>.`,
      });
    }

    /* 
      Check if tagged vendors all have emails
    */
    if (this.form.PurchaseTypeId == 1 && this.form.generalForm.reasonForNotUsingRFQ.id === environment.optForRFQReasonCode) {
      const vendorNumbers = validTaggedGroups
        .map((group) => {
          return group.vendors?.map((vendor) => vendor.vendorNumber) ?? [];
        })
        .flat();

      const vendorsWithInvalidEmail = vendorsList.filter(
        (vendor) =>
          vendor.VendorNumber &&
          !vendor.isDeleted &&
          !vendor.Strikethrough &&
          vendorNumbers.includes(vendor.VendorNumber) &&
          !vendor.EmailAddress &&
          !vendor.OtherPersonEmail
      );

      if (vendorsWithInvalidEmail.length > 0) {
        validationErrorMessages.push({
          category: ValidationErrorCategory.ItemVendor,
          message: `Please indicate a vendor email address for the following: <strong>${vendorsWithInvalidEmail
            .map((vendor) => vendor.Name)
            .join(', ')}</strong>`,
        });
      }
    }
    /* 
      Check for GEWCA
    */
    let hasCheckedGEWCAVendors = false;

    for (const item of itemList) {
      let isItemGEWCA = item?.AddItems?.IsGEWCA;

      // Skip if check was performed before for another item
      if (isItemGEWCA && !hasCheckedGEWCAVendors) {
        await this.validationService.validateGEWCA(
          validationErrorMessages,
          mpDocumentNumber!,
          vendorsList,
          ValidationErrorCategory.Tagging
        );
        hasCheckedGEWCAVendors = true;
      }
    }

    return validationErrorMessages;
  }

  public async onPostRFSReady() {
    const mpDocumentNumber = this.mpDocumentNumber;
    const dagId = this.fetchedAssignmentList.value.data?.dagId;

    if (!mpDocumentNumber || !dagId) {
      return {
        isSuccess: false,
        errorMessage: `Missing required request parameters for API: mpDocumentNumber = ${mpDocumentNumber}, dagId = ${dagId}`,
      };
    }
    const taggedGroupsObj = this.tagItemVendorService.taggedGroupsValue;
    if (mpDocumentNumber !== taggedGroupsObj.MPDocumentNumber) {
      return {
        isSuccess: false,
        errorMessage: `The MPDocumentNumber being readied does not match the MPDocumentNumber associated with the current taggings.`,
      };
    }

    const { validTaggedGroups, invalidTaggedGroups } =
      this.tagItemVendorService.getTaggedGroupsForPosting();

    const validationErrorMessages = await this.validateRFSTaggedGroups(
      validTaggedGroups,
      invalidTaggedGroups
    );

    if (
      Array.isArray(validationErrorMessages) &&
      validationErrorMessages.length > 0
    ) {
      return {
        validationErrorMessages: validationErrorMessages,
        isSuccess: false,
      };
    }

    let reqBody: ReadyTaggingRequest = {
      MPDocumentNumber: mpDocumentNumber,
      dagId: dagId,
      itemList: [
        ...itemListFrontendToBackendMapper(this.form.ItemList, this.form),
      ],
      vendorsList: [
        ...vendorsListFrontendToBackendMapper(this.form.VendorsToSource),
      ],
      taggedGroups: taggedGroupsFrontendToBackendMapper(validTaggedGroups),
    };

    try {
      const response = await lastValueFrom(
        this.backendApiService.readyTagging(reqBody)
      );

      const newRfsReady = _.cloneDeep(this.fetchedRFSReady.value);
      if (newRfsReady.data) {
        newRfsReady.data.isRFSReadied = true;
        this.fetchedRFSReady.next(newRfsReady);
      }

      return { isSuccess: true };
    } catch (err: any) {
      const errorMessage = err?.message ?? 'Something went wrong.';

      return { isSuccess: false, errorMessage: errorMessage };
    }
  }

  public async onPostRFSUnready() {
    const mpDocumentNumber = this.mpDocumentNumber;
    const dagId = this.fetchedAssignmentList.value.data?.dagId;
    if (!mpDocumentNumber || !dagId) {
      return {
        isSuccess: false,
        errorMessage: `Missing required request parameters for API: mpDocumentNumber = ${mpDocumentNumber}, dagId = ${dagId}`,
      };
    }

    try {
      let reqBody: UnreadyTaggingRequest = {
        MPDocumentNumber: mpDocumentNumber,
        dagId: dagId,
      };

      const response = await lastValueFrom(
        this.backendApiService.unreadyTagging(reqBody)
      );

      const newRfsReady = _.cloneDeep(this.fetchedRFSReady.value);
      if (newRfsReady.data) {
        newRfsReady.data.isRFSReadied = false;
        this.fetchedRFSReady.next(newRfsReady);
      }
      return { isSuccess: true };
    } catch (err: any) {
      const errorMessage = err?.message ?? 'Something went wrong.';
      return { isSuccess: false, errorMessage: errorMessage };
    }
  }

  private getSourcingStatusNameFromId(sourcingStatusId: number) {
    const foundSourcingStatus = SourcingStatusJSON.find(
      (entry) => entry.SourcingStatusId === sourcingStatusId
    );

    return foundSourcingStatus ? foundSourcingStatus.name : '';
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
